<template>
  <div class="comp-migration-detail-drawer">
    <jw-drawer
      width="100%"
      :visible="visible"
      :mask="false"
      :footer="false"
      :header="true"
      unmountOnClose
      @cancel="handleCancel"
      :drawer-style="{
        backgroundColor: 'var(--color-fill-2)',
        boxShadow: '0 1px 3px rgba(0,0,0,.3)'
      }"
    >
      <template #header>
        <div class="header">
          <span class="title">{{ tenantName }}</span>
          <jw-space>
            <jw-tooltip position="bottom" :content="$t('刷新')">
              <jw-button size="large" class="activeIcon" @click="handleRefresh">
                <template #icon>
                  <icon-refresh />
                </template>
              </jw-button>
            </jw-tooltip>
            <jw-tooltip position="bottom" :content="$t('关闭')">
              <jw-button @click="handleCancel" class="activeIcon">
                <template #icon>
                  <icon-close />
                </template>
              </jw-button>
            </jw-tooltip>
          </jw-space>
        </div>
      </template>
      <div class="detail-content">
        <div class="add-task-btn">
          <jw-button type="primary" @click="toggleIsModal">创建任务</jw-button>
        </div>
        <jw-card class="step-card">
          <jw-steps label-placement="vertical" changeable :current="currentStep" @change="handleStepChange">
            <jw-step>数据导出</jw-step>
            <jw-step :disabled="!migrationStatus?.importEnable">数据导入</jw-step>
          </jw-steps>
        </jw-card>
        <div class="detail-export" v-if="currentStep === 1">
          <jw-card title="全部">
            <!-- <template #extra>
              <jw-button type="primary" :disabled="!migrationStatus?.exportPartEnable" @click="handleExportAll">一键开启导出</jw-button>
            </template> -->
            <common-table :entity="dataEntity" :type="currentStep" :columns="exportColumns" :data="exportTableData" :loading="isTableLoading"> </common-table>
          </jw-card>
        </div>
        <div class="detail-import" v-if="currentStep === 2">
          <common-table :entity="dataEntity" :type="currentStep" :columns="importColumns" :data="exportTableData" :loading="isTableLoading"> </common-table>
        </div>
      </div>
    </jw-drawer>
  </div>
  <jw-modal :ok-loading="isModalLoading" title-align="start" :visible="isModal" title="创建补数据任务" @ok="submit" @cancel="toggleIsModal" :width="600">
    <jw-form auto-label-width :model="form" ref="formRef" @submit="handleSubmit">
      <jw-form-item field="module" hide-label hide-asterisk :rules="{ required: true, message: $t('请选择需要补充的模块') }">
        <jw-select placeholder="请选择需要补充的模块" v-model="form.module">
          <jw-option v-for="module in modules" :key="module?.code" :value="module?.code">{{ module?.name }}</jw-option>
        </jw-select>
      </jw-form-item>
      <template v-if="form.module === 'CONFIG_NUMBER_SEQUENCE'">
        <!-- 重复处理参数 -->
        <jw-form-item label="交易方（对方）编号起始值" :rules="{ required: true, message: $t('请输入') }">
          <jw-input v-model="form.transactionNo" :placeholder="$t('请输入')" :max-length="30" />
        </jw-form-item>
        <jw-form-item label="法人实体（我方）编号起始值" :rules="{ required: true, message: $t('请输入') }">
          <jw-input v-model="form.entityNo" :placeholder="$t('请输入')" :max-length="30" />
        </jw-form-item>
        <jw-form-item label="交易方对方风险编号起始值" :rules="{ required: true, message: $t('请输入') }">
          <jw-input v-model="form.riskNo" :placeholder="$t('请输入')" :max-length="30" />
        </jw-form-item>
      </template>

      <jw-form-item v-if="form.module === 'APPROVE_PERMISSIONS_DATA'" field="importType" hide-label hide-asterisk :rules="{ required: true, message: $t('请选择导入类型') }">
        <jw-radio-group type="button" v-model="form.importType">
          <jw-radio value="true">全量导入</jw-radio>
          <jw-radio value="false">部分导入</jw-radio>
        </jw-radio-group>
        <span class="text-tips" v-if="form.importType === 'false'">部分导入需要上传含有合同id的excel表</span>
      </jw-form-item>

      <jw-form-item v-if="form.importType === 'false'" field="file" hide-label hide-asterisk>
        <jw-upload :auto-upload="false" :accept="'.xlsx,.xls'" :max-count="1" @before-upload="handleBeforeUpload" @before-remove="handleRemove" :file-list="fileList">
          <template #upload-button>
            <div class="upload-btn"><span>*</span>上传文件</div>
          </template>
        </jw-upload>
      </jw-form-item>
    </jw-form>
  </jw-modal>
</template>

<script setup>
// TAG: 迁移工具
import { ref, toRefs, onMounted, reactive } from 'vue';
import { storeToRefs } from 'pinia';
import { useMigrationToolsStore } from '@/store';
import { migrationToolsService } from '@/api';
import { useEnvConfig } from '@/hooks/env-config';
import useColumns from '../../hooks/useColumns';
import useActions from '../../hooks/useActions';
import CommonTable from '../common-table/common-table.vue';
import { Message } from '@arco-design/web-vue';
import { useI18n } from 'vue-i18n';
// import { getToken } from '@/utils/auth';

const { t } = useI18n();
// props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  params: {
    type: String,
    default: ''
  }
});
// emits
const emits = defineEmits(['close']);
// =====data=====
const currentStep = ref(1);
// const token = getToken() || null;

// const uploadParams = {
//    headers: {
//     authorization: `Bearer ${token}`
//   },
//   actionUrl: '/api/uploadFile',
// }

const dataEntity = ref(props.params.entity);
const { visible, params } = toRefs(props);
const { importColumns, exportColumns } = useColumns();

const migrationToolsStore = useMigrationToolsStore();
const { isTableLoading, modules, exportTableData, migrationStatus } = storeToRefs(migrationToolsStore);

const { fieldMappings } = useEnvConfig(props.params.entity);
const tenantId = params.value.row[fieldMappings.tenantId];
const tenantName = params.value.row[fieldMappings.tenantName];
migrationToolsStore.setTenantId(tenantId);

const { handleRefresh, getDataList, getModules } = useActions({ currentStep, dataEntity });

// =====mounted=====
onMounted(() => {
  handleRefresh();
  getModules();
});

// =====methods=====
// step切换
const handleStepChange = (val) => {
  currentStep.value = val;
  getDataList();
};
// 弹窗关闭
const handleCancel = () => {
  emits('close');
};

const form = reactive({
  module: '',
  transactionNo: '', //交易方编号
  entityNo: '', //实体编号
  riskNo: '', //交易方对方风险编号
  importType: 'true', // 导入类型
  file: null // 文件
});
const formRef = ref();
const isModal = ref(false);
const fileList = ref([]); // 上传文件列表

const toggleIsModal = () => {
  formRef.value?.resetFields?.();
  fileList.value = [];
  isModal.value = !isModal.value;
};
const submit = () => {
  formRef.value.handleSubmit();
};

// 删除
const handleRemove = () => {
  fileList.value = [];
};

// 上传拦截
const handleBeforeUpload = (file) => {
  // 拦截上传，保存到 fileList，而不是立刻上传
  fileList.value = [
    {
      uid: file.uid, // 每个文件的唯一标识（upload 组件需要）
      name: file.name, // 文件名
      status: 'done', // 状态: 'uploading' | 'done' | 'error'
      file: file // 原始 File 对象，提交时要用到
    }
  ]; // 如果只允许一个文件，可以直接覆盖
  return false; // 返回 false → 阻止自动上传
};
const isModalLoading = ref(false);
const handleSubmit = async ({ values, errors }) => {
  console.log(values, errors);

  try {
    if (!errors) {
      if (values.importType === 'false' && fileList.value.length === 0) {
        Message.error('请上传文件');
        return;
      }
      isModalLoading.value = true;

      const formData = new FormData();

      const taskType = {
        taskType: values?.module,
        isAll: values?.importType,
        tenantId: tenantId
      };
      if (form.module === 'CONFIG_NUMBER_SEQUENCE') {
        taskType.tradingInitialValue = form.transactionNo;
        taskType.legalInitialValue = form.entityNo;
        taskType.riskInitialValue = form.riskNo;
      }
      formData.set('taskType', JSON.stringify(taskType)); // 数据合并放入taskType中
      // formData.set('isAll', values?.importType); // 导入类型
      // formData.set('tenantId', tenantId); // 导入类型

      if (values?.importType === 'false') {
        formData.set('file', fileList.value[0].file); // 上传文件
      }

      // const params = {
      //   data: formData
      // };

      // 打印 FormData 正确方式
      for (const [key, value] of formData.entries()) {
        console.log(key, value);
      }

      const res = await migrationToolsService.createTask(formData);
      if (!res?.error) {
        if (!res?.result?.result) {
          Message.error(res?.result?.msg || t('操作失败'));
        } else {
          Message.success(res?.result?.msg || t('操作成功'));
          toggleIsModal();
          handleRefresh();
        }
      } else {
        Message.error(res?.error?.msg || t('操作失败'));
      }
      isModalLoading.value = false;
    }
  } catch (e) {
    console.log(e);
  }
};
</script>
<style scoped lang="less">
@import './migration-v-2-drawer.less';
</style>
